import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import SkeletonBox from '@/components/SkeletonBox'
import { APIWeb } from '@/api'
import KycPersonalInfoForm from './components/KycPersonalInfoForm'
import KycDocumentUploadForm from './components/KycDocumentUploadForm'
import { type KycFormData, convertDetailToFormData, initialFormData } from './types'

type TabType = 'personal' | 'document'

export default function ViewKyc() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<TabType>('personal')
  const [formData, setFormData] = useState<KycFormData>(initialFormData)
  const [loading, setLoading] = useState(false)

  // 从路由state获取kycId并请求详情
  useEffect(() => {
    const kycId = location.state?.kycId
    if (kycId) {
      fetchKycDetail(kycId)
    }
  }, [location.state])

  // 获取KYC详情
  const fetchKycDetail = async (id: number) => {
    try {
      setLoading(true)
      const response = await APIWeb.kycDetail({ id })
      if (response?.kyc) {
        setFormData(convertDetailToFormData(response.kyc))
      }
    } catch (error) {
      console.error('获取KYC详情失败:', error)
      Toast.fail(t('获取数据失败'))
    } finally {
      setLoading(false)
    }
  }

  // 返回上一页
  const handleBack = () => {
    navigate(-1)
  }

  // 空的更新函数（只读模式不更新）
  const noopUpdateFormData = () => {}

  return (
    <PageLayout>
      <div className="flex flex-col h-full">
        <Header
          leftChildren={<Back onClick={handleBack} />}
          centerChildren={<span className="text-base font-medium">{t('KYC信息')}</span>}
        />

        {/* Tab切换 */}
        <div className="flex px-5 pt-4 gap-2">
          <button
            onClick={() => setActiveTab('personal')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-none cursor-pointer ${
              activeTab === 'personal'
                ? 'bg-[var(--primary-color)] text-white'
                : 'bg-[#F2F3F5] text-[#86909C]'
            }`}
          >
            {t('个人信息')}
          </button>
          <button
            onClick={() => setActiveTab('document')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-none cursor-pointer ${
              activeTab === 'document'
                ? 'bg-[var(--primary-color)] text-white'
                : 'bg-[#F2F3F5] text-[#86909C]'
            }`}
          >
            {t('证件信息')}
          </button>
        </div>

        <div className="flex-1 px-5 pt-6 pb-8 overflow-y-auto">
          {loading ? (
            <div className="space-y-4">
              {/* 国家/地区 */}
              <div>
                <SkeletonBox className="h-4 w-20 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 姓氏 & 名字 */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
              </div>
              {/* 性别 */}
              <div>
                <SkeletonBox className="h-4 w-12 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 手机号 */}
              <div>
                <SkeletonBox className="h-4 w-16 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 电子邮箱 */}
              <div>
                <SkeletonBox className="h-4 w-20 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 国籍 */}
              <div>
                <SkeletonBox className="h-4 w-32 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 出生日期 */}
              <div>
                <SkeletonBox className="h-4 w-24 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 职务 */}
              <div>
                <SkeletonBox className="h-4 w-12 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 省份 & 城市 */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
              </div>
              {/* 详细地址 */}
              <div>
                <SkeletonBox className="h-4 w-20 rounded mb-2" />
                <SkeletonBox className="h-24 rounded-2.5" />
              </div>
            </div>
          ) : activeTab === 'personal' ? (
            <KycPersonalInfoForm
              formData={formData}
              updateFormData={noopUpdateFormData}
              readonly={true}
            />
          ) : (
            <KycDocumentUploadForm
              formData={formData}
              updateFormData={noopUpdateFormData}
              readonly={true}
              showHandheld={true}
            />
          )}
        </div>
      </div>
    </PageLayout>
  )
}

